import { useState } from 'react'
import { ExternalLink, Send } from 'lucide-react'

import {
  POST_STATUS_LABELS,
  POST_STATUS_VARIANT,
  PROVIDER_LABELS,
  type IntegrationPost,
} from '@/types/integrations'
import {
  useBoatPosts,
  useConnections,
  usePostBoatManually,
} from '@/features/integrations/hooks'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface PostBoatDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  boatId: string
  boatLabel: string
  price: string
}

// PostBoatDialog composes a manual listing post from the boat detail page:
// pick a connection, optionally edit the message, send, and see this boat's
// post history.
export function PostBoatDialog({ open, onOpenChange, boatId, boatLabel, price }: PostBoatDialogProps) {
  const { data: connections = [] } = useConnections()
  const { data: posts = [] } = useBoatPosts(boatId)
  const postMutation = usePostBoatManually()

  const postable = connections.filter((c) => c.enabled)
  const [connectionId, setConnectionId] = useState('')
  const [message, setMessage] = useState('')

  // Reset whenever the dialog opens for a (possibly different) boat.
  const formKey = `${open}:${boatId}`
  const [prevFormKey, setPrevFormKey] = useState(formKey)
  if (prevFormKey !== formKey) {
    setPrevFormKey(formKey)
    if (open) {
      setConnectionId(postable[0]?.id ?? '')
      setMessage(`${boatLabel} — ${price}. Come see it at the marina!`)
    }
  }

  const submit = async () => {
    if (!connectionId) return
    try {
      await postMutation.mutateAsync({ boatId, body: { connection_id: connectionId, message: message.trim() || undefined } })
      onOpenChange(false)
    } catch {
      // error surfaces via the global mutation toast
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Post “{boatLabel}”</DialogTitle>
          <DialogDescription>
            Sends now to the chosen channel — the send log records the outcome.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Channel</label>
            <Select value={connectionId} onValueChange={setConnectionId}>
              <SelectTrigger>
                <SelectValue placeholder={postable.length ? 'Select channel' : 'No active connections'} />
              </SelectTrigger>
              <SelectContent>
                {postable.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name} · {PROVIDER_LABELS[c.provider]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Message</label>
            <Textarea
              rows={4}
              placeholder={`{{year}} {{make}} {{model}} — {{price}} placeholders are filled if left as-is`}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Placeholders{' '}
              <code className="rounded bg-muted px-1">
                {'{{make}} {{model}} {{year}} {{price}}'}
              </code>{' '}
              render from the boat. The cover photo (if any) is attached via a signed public URL.
            </p>
          </div>

          {posts.length > 0 && (
            <div className="space-y-2">
              <label className="text-sm font-medium">Post history</label>
              <div className="max-h-40 space-y-1.5 overflow-y-auto rounded-md border p-2">
                {posts.map((p: IntegrationPost) => (
                  <div key={p.id} className="flex items-center justify-between gap-2 text-sm">
                    <span className="truncate">{p.connection}</span>
                    <span className="flex items-center gap-2">
                      <Badge variant={POST_STATUS_VARIANT[p.status]}>
                        {POST_STATUS_LABELS[p.status]}
                      </Badge>
                      {p.permalink && (
                        <a
                          href={p.permalink}
                          target="_blank"
                          rel="noreferrer"
                          className="text-muted-foreground hover:text-foreground"
                          title="Open post"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!connectionId || postMutation.isPending}>
            <Send className="mr-2 h-4 w-4" />
            {postMutation.isPending ? 'Posting…' : 'Post now'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
